import { useState, useEffect, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../App';
import {
  calculateMAO, calculateROI, calculateCapRate, calculateCashFlow,
  calculateMortgage, calculateHoldingCosts, calculateDealScore,
  formatCurrency, formatPercent
} from '../utils/calculations';
import { searchProperty, getAIInsight, getAIValuation } from '../utils/api';
import RehabEstimator from '../components/RehabEstimator';
import MortgageCalculator from '../components/MortgageCalculator';
import StreetViewWidget from '../components/StreetViewWidget';
import PDFReport from '../components/PDFReport';
import ContractPDF from '../components/ContractPDF';
import HoldingCostEstimator from '../components/HoldingCostEstimator';
import AddressAutocomplete from '../components/AddressAutocomplete';

const WIZARD_KEY = 'proparv_wizard_state';
const STEPS = { SEARCH: 1, PREVIEW: 2, RESULTS: 3 };

// ── Helpers ──────────────────────────────────────────────────
function StepBadge({ current, total, label }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 24 }}>
      <span style={{
        background: 'var(--primary)', color: '#fff', borderRadius: 6,
        padding: '3px 10px', fontSize: '0.75rem', fontWeight: 700, letterSpacing: '0.04em'
      }}>
        Step {current} of {total}
      </span>
      <span style={{ fontSize: '0.9rem', color: 'var(--text-secondary)', fontWeight: 600 }}>{label}</span>
    </div>
  );
}

function Section({ title, children, defaultOpen = true }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="card" style={{ marginBottom: 20 }}>
      <button
        onClick={() => setOpen(!open)}
        style={{
          width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          background: 'none', border: 'none', cursor: 'pointer', padding: 0, fontFamily: 'inherit'
        }}
      >
        <span style={{ fontSize: '1rem', fontWeight: 700, color: 'var(--text-primary)' }}>{title}</span>
        <span style={{ color: 'var(--text-muted)', fontSize: 16, transition: 'transform 0.2s', transform: open ? 'rotate(0deg)' : 'rotate(-90deg)' }}>
          ▼
        </span>
      </button>
      {open && <div style={{ marginTop: 20 }}>{children}</div>}
    </div>
  );
}

function MetricCard({ label, value, sub, color = 'neutral' }) {
  const colors = {
    positive: 'var(--success)', negative: 'var(--danger)', warning: 'var(--warning)',
    blue: 'var(--primary-light)', neutral: 'var(--text-primary)'
  };
  return (
    <div style={{ background: 'var(--bg-dark)', borderRadius: 10, padding: '16px', border: '1px solid var(--border)', textAlign: 'center' }}>
      <div style={{ fontSize: '1.4rem', fontWeight: 800, color: colors[color], lineHeight: 1, marginBottom: 4 }}>{value}</div>
      <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.07em', fontWeight: 500 }}>{label}</div>
      {sub && <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: 4 }}>{sub}</div>}
    </div>
  );
}

// ── Main Component ────────────────────────────────────────────
export default function Analyze() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  // ── Wizard state ─────────────────────────────────────────
  const [step, setStep] = useState(STEPS.SEARCH);
  const [addressInput, setAddressInput] = useState(searchParams.get('address') || '');
  const [placeInfo, setPlaceInfo] = useState({ lat: null, lng: null });
  const [propertyData, setPropertyData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Editable fields (step 2)
  const [editableFields, setEditableFields] = useState({ beds: '', baths: '', sqft: '', yearBuilt: '', lotSize: '' });
  // Comp filters (UI only — does not affect API)
  const [compInstructions, setCompInstructions] = useState('');
  const [compFlags, setCompFlags]               = useState({ matchBedsBaths: false, builtWithin10: false, hasPool: false, schoolDistrict: false });
  const [compMode, setCompMode]                 = useState('arv');
  const [compDistMode, setCompDistMode]         = useState('auto');
  const [compDistance, setCompDistance]         = useState(1.0);
  const [compTimeFrame, setCompTimeFrame]       = useState(12);
  const [compStatus, setCompStatus]             = useState({ mlsActive: false, mlsPending: false, mlsSold: true, publicRecord: false });
  const [compPropertyType, setCompPropertyType] = useState('auto');
  // Photo carousel + lightbox
  const [photoIndex, setPhotoIndex] = useState(0);
  const [lightbox, setLightbox]     = useState(false);
  const [lbZoomed, setLbZoomed]     = useState(false);

  // Lightbox keyboard nav
  useEffect(() => {
    if (!lightbox) return;
    const imgs = propertyData?.images?.length ? propertyData.images : (propertyData?.imageUrl ? [propertyData.imageUrl] : []);
    const onKey = (e) => {
      if (e.key === 'Escape')      { setLightbox(false); setLbZoomed(false); }
      if (e.key === 'ArrowRight')  setPhotoIndex(i => (i + 1) % imgs.length);
      if (e.key === 'ArrowLeft')   setPhotoIndex(i => (i - 1 + imgs.length) % imgs.length);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [lightbox, propertyData]);

  // Comp filters (step 2)
  const [compFilters, setCompFilters] = useState({
    maxDistanceMi: 1.0, timeframeMonths: 12, status: 'sold', propertyType: 'Any'
  });

  // Comps (step 3)
  const [comps, setComps] = useState([]);
  const [selectedComps, setSelectedComps] = useState([]);
  const [compsLoading, setCompsLoading] = useState(false);
  const [compsError, setCompsError] = useState('');
  const [arvFromComps, setArvFromComps] = useState(0);

  // AI-powered estimates (from web search)
  const [aiMonthlyRent, setAiMonthlyRent] = useState(0);
  const [aiRentLow,     setAiRentLow]     = useState(0);
  const [aiRentHigh,    setAiRentHigh]    = useState(0);
  const [aiArvLow,      setAiArvLow]      = useState(0);
  const [aiArvHigh,     setAiArvHigh]     = useState(0);
  const [aiRehabLow,    setAiRehabLow]    = useState(0);
  const [aiRehabHigh,   setAiRehabHigh]   = useState(0);

  // Results (step 4)
  const [strategy, setStrategy] = useState('flip');
  const [purchasePrice, setPurchasePrice] = useState('');
  const [downPct, setDownPct] = useState(20);
  const [interestRate, setInterestRate] = useState(7.5);
  const [loanTerm, setLoanTerm] = useState(30);
  const [rehabCost, setRehabCost] = useState(0);
  const [holdingCosts, setHoldingCosts] = useState({ months: 6, taxes: 250, insurance: 150, utilities: 200, hoa: 0, other: 100 });
  const [aiInsight, setAiInsight] = useState('');
  const [aiLoading, setAiLoading] = useState(false);
  const [userImages, setUserImages] = useState([]);

  // Strategy-specific inputs
  const [assignmentFee, setAssignmentFee]   = useState(10000);
  const [targetProfit,  setTargetProfit]    = useState(30000);
  const [targetCocROI,  setTargetCocROI]    = useState(10);
  const [refinanceLTV,  setRefinanceLTV]    = useState(75);
  const [nightlyRate,   setNightlyRate]     = useState(120);
  const [occupancyRate, setOccupancyRate]   = useState(70);

  // Auth gate state
  const [gateError, setGateError] = useState('');
  const [gateChecking, setGateChecking] = useState(false);

  // ── Session restore ───────────────────────────────────────
  useEffect(() => {
    const saved = sessionStorage.getItem(WIZARD_KEY);
    if (saved) {
      try {
        const s = JSON.parse(saved);
        if (s.addressInput) setAddressInput(s.addressInput);
        if (s.placeInfo)    setPlaceInfo(s.placeInfo);
        if (s.propertyData) {
          setPropertyData(s.propertyData);
          setEditableFields({
            beds: s.editableFields?.beds ?? s.propertyData.beds ?? '',
            baths: s.editableFields?.baths ?? s.propertyData.baths ?? '',
            sqft: s.editableFields?.sqft ?? s.propertyData.sqft ?? '',
            yearBuilt: s.editableFields?.yearBuilt ?? s.propertyData.yearBuilt ?? '',
            lotSize: s.editableFields?.lotSize ?? s.propertyData.lotSize ?? ''
          });
        }
        if (s.compFilters)  setCompFilters(s.compFilters);
        if (s.comps?.length) { setComps(s.comps); setSelectedComps(s.selectedComps || s.comps.map((_, i) => i)); }
        if (s.arvFromComps) setArvFromComps(s.arvFromComps);
        if (s.purchasePrice) setPurchasePrice(s.purchasePrice);
        if (s.step && s.step >= STEPS.PREVIEW) setStep(s.step);
        sessionStorage.removeItem(WIZARD_KEY);
      } catch {}
    } else {
      const addr = searchParams.get('address');
      if (addr) { setAddressInput(addr); handleSearch(addr); }
    }
  }, []);

  // ── Persist state before navigating away ─────────────────
  const saveWizardState = (overrideStep) => {
    sessionStorage.setItem(WIZARD_KEY, JSON.stringify({
      step: overrideStep ?? step,
      addressInput, placeInfo, propertyData,
      editableFields, compFilters,
      comps, selectedComps, arvFromComps, purchasePrice
    }));
  };

  // ── Step 1: Search ────────────────────────────────────────
  const handlePlaceSelect = (address, info) => {
    const coords = { lat: info.lat, lng: info.lng };
    setPlaceInfo(coords);
    handleSearch(address, coords);
  };

  const handleSearch = async (addr, coords = null) => {
    const searchAddr = addr || addressInput;
    if (!searchAddr.trim()) return;
    setLoading(true);
    setError('');
    setPropertyData(null);
    setStep(STEPS.SEARCH);
    if (!coords) setPlaceInfo({ lat: null, lng: null });
    try {
      const data = await searchProperty(searchAddr.trim());
      setPropertyData(data);
      setEditableFields({
        beds: data.beds || '', baths: data.baths || '',
        sqft: data.sqft || '', yearBuilt: data.yearBuilt || '', lotSize: data.lotSize || ''
      });
      // Reset stale values from any previous analysis
      setPhotoIndex(0);
      setArvFromComps(0);
      setAiArvLow(0); setAiArvHigh(0);
      setAiRehabLow(0); setAiRehabHigh(0);
      setAiMonthlyRent(0); setAiRentLow(0); setAiRentHigh(0);
      setPurchasePrice('');
      setStep(STEPS.PREVIEW);

    } catch (err) {
      setError('Failed to fetch property data. Please try again.');
    }
    setLoading(false);
  };

  // ── Step 2: Auth gate check before comps ─────────────────
  const handleConfirmInfo = async () => {
    setGateError('');
    setGateChecking(true);

    if (!user) {
      saveWizardState(STEPS.PREVIEW);
      navigate('/login?next=/analyze');
      return;
    }

    // Check subscription
    try {
      const token = localStorage.getItem('proparv_token');
      const r = await fetch('/api/subscription/status', {
        headers: { Authorization: `Bearer ${token}` }
      });
      const sub = await r.json();

      const hasActiveSub = sub.status === 'active' && sub.tier !== 'free';
      if (!hasActiveSub) {
        saveWizardState(STEPS.PREVIEW);
        navigate('/pricing?next=/analyze');
        return;
      }
      if (sub.searchLimit !== -1 && sub.searchesUsed >= sub.searchLimit) {
        setGateError(`You've used all ${sub.searchLimit} comp searches this month. Upgrade your plan to continue.`);
        setGateChecking(false);
        return;
      }
    } catch {
      setGateError('Unable to verify subscription. Please try again.');
      setGateChecking(false);
      return;
    }

    // Fetch comps
    setGateChecking(false);
    await fetchComps();
  };

  // ── Step 2 → 3: AI Valuation + Rent — go straight to results ──
  const fetchComps = async () => {
    setCompsLoading(true);
    setCompsError('');
    try {
      const sqft      = editableFields.sqft      || propertyData?.sqft      || 0;
      const beds      = editableFields.beds      || propertyData?.beds      || 3;
      const baths     = editableFields.baths     || propertyData?.baths     || 2;
      const yearBuilt = editableFields.yearBuilt || propertyData?.yearBuilt || '';

      const valuationArgs = {
        listPrice:    0,
        beds, baths, sqft, yearBuilt,
        propertyType: propertyData?.propertyType || 'Single Family',
        strategy
      };

      const result = await getAIValuation(addressInput, valuationArgs);

      const fetchedComps = result.comps || [];
      setComps(fetchedComps);

      if (result.arv) {
        setArvFromComps(result.arv);
        setPurchasePrice(prev => (!prev || prev === '0') ? String(Math.round(result.arv * 0.72)) : prev);
      }
      if (result.arvLow)   setAiArvLow(result.arvLow);
      if (result.arvHigh)  setAiArvHigh(result.arvHigh);
      if (result.rehabLow)  setAiRehabLow(result.rehabLow);
      if (result.rehabHigh) setAiRehabHigh(result.rehabHigh);

      const rentLow  = result.rentLow  || 0;
      const rentHigh = result.rentHigh || 0;
      const rentMid  = result.rentMid  || (rentLow && rentHigh ? Math.round((rentLow + rentHigh) / 2) : 0);
      if (rentLow)  setAiRentLow(rentLow);
      if (rentHigh) setAiRentHigh(rentHigh);
      if (rentMid)  setAiMonthlyRent(rentMid);

      setStep(STEPS.RESULTS);

      // Save to recent analyses (max 20, newest first, deduplicated by address)
      const record = {
        id: Date.now(),
        address: addressInput,
        arv: result.arv || 0,
        price: propertyData?.price || 0,
        imageUrl: userImages[0] || propertyData?.images?.[0] || propertyData?.imageUrl || null,
        beds:      editableFields.beds      || propertyData?.beds      || '',
        baths:     editableFields.baths     || propertyData?.baths     || '',
        sqft:      editableFields.sqft      || propertyData?.sqft      || '',
        yearBuilt: editableFields.yearBuilt || propertyData?.yearBuilt || '',
        compsCount: (result.comps || []).length,
        analyzedAt: new Date().toISOString(),
      };
      const storageKey = `proparv_analyzed_${user?.id || 'guest'}`;
      const prev    = JSON.parse(localStorage.getItem(storageKey) || '[]');
      const updated = [record, ...prev.filter(r => r.address !== addressInput)].slice(0, 20);
      localStorage.setItem(storageKey, JSON.stringify(updated));

    } catch (err) {
      const errData = err.response?.data;
      if (errData?.error === 'usage_limit_reached' || errData?.error === 'subscription_required') {
        saveWizardState(STEPS.PREVIEW);
        navigate('/pricing?next=/analyze');
      } else if (err.response?.status === 401) {
        saveWizardState(STEPS.PREVIEW);
        navigate('/login?next=/analyze');
      } else {
        const msg = errData?.message || errData?.error || err.message || 'Analysis failed. Please try again.';
        setCompsError(msg);
      }
    } finally {
      setCompsLoading(false);
    }
  };


  // ── Extract city/state for AI rehab estimator ────────────
  const extractLocation = (address) => {
    const parts = (address || '').split(',').map(s => s.trim());
    const city  = parts[1] || '';
    const st    = (parts[2] || '').replace(/usa/i, '').trim().split(' ')[0];
    return [city, st].filter(Boolean).join(', ') || 'US market';
  };
  const propertyLocation = extractLocation(propertyData?.address || addressInput);

  // ── Step 4: Calculations ──────────────────────────────────
  const arv             = arvFromComps || propertyData?.arv || 0;
  const rent            = aiMonthlyRent || propertyData?.rentEstimate || 0;
  const pp              = parseFloat(purchasePrice) || 0;
  const downPayment     = pp * (downPct / 100);
  const loanAmount      = pp - downPayment;
  const holdingResult   = calculateHoldingCosts(holdingCosts.months, holdingCosts.taxes, holdingCosts.insurance, holdingCosts.utilities, holdingCosts.hoa, holdingCosts.other);
  const mortgageResult  = calculateMortgage(loanAmount, interestRate, loanTerm);
  const mao             = calculateMAO(arv, rehabCost, strategy);
  const totalInvestment = downPayment + rehabCost + holdingResult.total;
  const sellingCosts    = arv * 0.06;
  const grossProfit     = arv - pp - rehabCost;
  const profit          = grossProfit - holdingResult.total - sellingCosts;
  const roi             = calculateROI(profit, totalInvestment);
  const annualNOI       = (rent - mortgageResult.monthly - holdingResult.monthly) * 12;
  const capRate         = calculateCapRate(annualNOI, arv || pp);
  const cashFlow        = calculateCashFlow(rent, { mortgage: mortgageResult.monthly, taxes: holdingCosts.taxes, insurance: holdingCosts.insurance, utilities: holdingCosts.utilities, hoa: holdingCosts.hoa });
  // ── Strategy-specific offer calculations ─────────────────
  const strategyCalc = (() => {
    switch (strategy) {
      case 'flip': {
        const offerPrice = Math.max(0, arv - rehabCost - holdingResult.total - sellingCosts - targetProfit);
        const netProfit  = arv - offerPrice - rehabCost - holdingResult.total - sellingCosts;
        const flipROI    = totalInvestment > 0 ? ((netProfit / (offerPrice * (downPct / 100) + rehabCost + holdingResult.total)) * 100) : 0;
        return { offerPrice, netProfit, flipROI, label: 'Fix & Flip' };
      }
      case 'hold': {
        // Reverse-calc: Annual NOI / target cap rate = max offer
        const annualExpenses = holdingResult.monthly * 12;
        const annualRent     = rent * 12 * 0.95; // 5% vacancy
        const noi            = annualRent - annualExpenses;
        const offerPrice     = noi > 0 ? Math.round(noi / (targetCocROI / 100)) : mao;
        const monthlyCF      = cashFlow;
        const annualCF       = monthlyCF * 12;
        return { offerPrice, monthlyCF, annualCF, capRate, label: 'Buy & Hold' };
      }
      case 'brrrr': {
        const refAmount  = Math.round(arv * (refinanceLTV / 100));
        const totalCost  = pp + rehabCost;
        const cashLeft   = Math.max(0, totalCost - refAmount);
        const cashOut    = Math.min(refAmount, totalCost);
        const equity     = arv - totalCost;
        const offerPrice = Math.max(0, refAmount - rehabCost);
        return { offerPrice, refAmount, cashLeft, cashOut, equity, label: 'BRRRR' };
      }
      case 'wholesale': {
        const offerPrice     = Math.max(0, arv * 0.65 - rehabCost - assignmentFee);
        const endBuyerMAO    = Math.round(arv * 0.65 - rehabCost);
        return { offerPrice, endBuyerMAO, assignmentFee, label: 'Wholesale' };
      }
      case 'airbnb': {
        const monthlyRevenue = Math.round(nightlyRate * 30 * (occupancyRate / 100));
        const mgmtFee        = Math.round(monthlyRevenue * 0.20);
        const monthlyExp     = mortgageResult.monthly + mgmtFee + holdingResult.monthly;
        const strCashFlow    = monthlyRevenue - monthlyExp;
        const offerPrice     = mao; // ARV × 0.72 - rehab
        return { offerPrice, monthlyRevenue, mgmtFee, monthlyExp, strCashFlow, label: 'Airbnb / STR' };
      }
      default:
        return { offerPrice: mao, label: strategy };
    }
  })();

  const recommendedOffer = strategyCalc.offerPrice;
  const dealRating = (() => {
    if (!pp || !arv) return null;
    const spread = (arv - pp - rehabCost) / arv;
    if (spread >= 0.25) return { label: 'Good Deal', color: '#10b981' };
    if (spread >= 0.10) return { label: 'Average Deal', color: '#f59e0b' };
    return { label: 'Risky Deal', color: '#ef4444' };
  })();

  const dealData = {
    address: propertyData?.address || addressInput,
    dealScore: calculateDealScore(arv, pp, rehabCost, rent),
    arv, purchasePrice: pp, rehabCost, mao, recommendedOffer,
    roi, cashFlow, capRate, profit, grossProfit,
    monthlyMortgage: mortgageResult.monthly, holdingCosts: holdingResult.total,
    totalInvestment, downPayment, sellingCosts,
    imageUrl: propertyData?.images?.[0] || propertyData?.imageUrl || null,
    beds: editableFields.beds || propertyData?.beds,
    baths: editableFields.baths || propertyData?.baths,
    sqft: editableFields.sqft || propertyData?.sqft,
    yearBuilt: editableFields.yearBuilt || propertyData?.yearBuilt,
    propertyType: propertyData?.propertyType,
    lotSize: editableFields.lotSize || propertyData?.lotSize,
    lastSoldPrice: propertyData?.lastSoldPrice,
    rentEstimate: rent, strategy, strategyCalc, aiInsight, holdingMonths: holdingCosts.months,
    holdingBreakdown: holdingResult.breakdown, holdingMonthly: holdingResult.monthly
  };

  const handleGetAI = async () => {
    setAiLoading(true);
    setAiInsight('');
    try {
      const result = await getAIInsight(dealData);
      setAiInsight(result.insight || '');
    } catch {
      setAiInsight('AI analysis is temporarily unavailable. Please try again.');
    }
    setAiLoading(false);
  };

  const saveDeal = () => {
    const saved = JSON.parse(localStorage.getItem('proparv_deals') || '[]');
    saved.unshift({ ...dealData, id: Date.now(), savedAt: new Date().toISOString() });
    localStorage.setItem('proparv_deals', JSON.stringify(saved.slice(0, 50)));
    alert('Deal saved to dashboard!');
  };

  // ── Render ────────────────────────────────────────────────
  const _lbImgs = propertyData?.images?.length ? propertyData.images : (propertyData?.imageUrl ? [propertyData.imageUrl] : []);

  return (
    <div className="page">

      {/* ── LIGHTBOX OVERLAY ── */}
      {lightbox && _lbImgs.length > 0 && (
        <div
          onClick={() => { if (!lbZoomed) { setLightbox(false); setLbZoomed(false); } }}
          style={{
            position: 'fixed', inset: 0, zIndex: 9999,
            background: 'rgba(0,0,0,0.94)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            flexDirection: 'column'
          }}
        >
          {/* Close */}
          <button onClick={() => { setLightbox(false); setLbZoomed(false); }} style={{
            position: 'absolute', top: 18, right: 20, background: 'rgba(255,255,255,0.12)',
            border: 'none', color: '#fff', fontSize: 26, width: 42, height: 42, borderRadius: '50%',
            cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
            backdropFilter: 'blur(6px)', zIndex: 1
          }}>×</button>

          {/* Counter */}
          <div style={{ position: 'absolute', top: 22, left: 22, color: 'rgba(255,255,255,0.7)', fontSize: '0.85rem', fontWeight: 600 }}>
            {photoIndex + 1} / {_lbImgs.length}
          </div>

          {/* Main image */}
          <div
            onClick={e => { e.stopPropagation(); setLbZoomed(z => !z); }}
            style={{ cursor: lbZoomed ? 'zoom-out' : 'zoom-in', maxWidth: '92vw', maxHeight: '82vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          >
            <img
              src={_lbImgs[photoIndex]}
              alt=""
              referrerPolicy="no-referrer"
              style={{
                maxWidth: lbZoomed ? '100vw' : '90vw',
                maxHeight: lbZoomed ? '95vh' : '78vh',
                objectFit: 'contain',
                borderRadius: lbZoomed ? 0 : 10,
                transition: 'all 0.25s ease',
                userSelect: 'none'
              }}
            />
          </div>

          {/* Prev / Next */}
          {_lbImgs.length > 1 && (<>
            <button
              onClick={e => { e.stopPropagation(); setPhotoIndex(i => (i - 1 + _lbImgs.length) % _lbImgs.length); setLbZoomed(false); }}
              style={{ position: 'absolute', left: 16, top: '50%', transform: 'translateY(-50%)', background: 'rgba(255,255,255,0.12)', border: 'none', color: '#fff', fontSize: 32, width: 52, height: 52, borderRadius: '50%', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', backdropFilter: 'blur(6px)' }}
            >‹</button>
            <button
              onClick={e => { e.stopPropagation(); setPhotoIndex(i => (i + 1) % _lbImgs.length); setLbZoomed(false); }}
              style={{ position: 'absolute', right: 16, top: '50%', transform: 'translateY(-50%)', background: 'rgba(255,255,255,0.12)', border: 'none', color: '#fff', fontSize: 32, width: 52, height: 52, borderRadius: '50%', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', backdropFilter: 'blur(6px)' }}
            >›</button>
          </>)}

          {/* Thumbnail strip */}
          {_lbImgs.length > 1 && (
            <div style={{ display: 'flex', gap: 8, marginTop: 16, maxWidth: '90vw', overflowX: 'auto', padding: '4px 0' }}>
              {_lbImgs.map((url, i) => (
                <img
                  key={i}
                  src={url}
                  alt=""
                  referrerPolicy="no-referrer"
                  onClick={e => { e.stopPropagation(); setPhotoIndex(i); setLbZoomed(false); }}
                  style={{
                    width: 72, height: 52, objectFit: 'cover', borderRadius: 6, cursor: 'pointer', flexShrink: 0,
                    border: i === photoIndex ? '2px solid #fff' : '2px solid transparent',
                    opacity: i === photoIndex ? 1 : 0.55, transition: 'all 0.15s'
                  }}
                />
              ))}
            </div>
          )}

          {/* Hint */}
          <div style={{ position: 'absolute', bottom: 14, color: 'rgba(255,255,255,0.35)', fontSize: '0.72rem' }}>
            Click image to zoom · Arrow keys to navigate · Esc to close
          </div>
        </div>
      )}

      <div className="container page-content">

        {/* ══════════════════════════════════════════════════
            STEP 1 — SEARCH
        ══════════════════════════════════════════════════ */}
        {step === STEPS.SEARCH && (
          <div style={{ maxWidth: 640, margin: '60px auto 0' }}>
            <div className="card">
              <h1 style={{ fontSize: '1.5rem', fontWeight: 800, marginBottom: 6, color: 'var(--text-primary)' }}>
                Real Estate Deal Analyzer
              </h1>
              <p style={{ color: 'var(--text-secondary)', marginBottom: 20, fontSize: '0.9rem' }}>
                Enter any US property address to get comps, ARV, and a full deal analysis.
              </p>
              <div style={{ display: 'flex', gap: 10 }}>
                <AddressAutocomplete
                  value={addressInput}
                  onChange={setAddressInput}
                  onSelect={handlePlaceSelect}
                  disabled={loading}
                />
                <button
                  onClick={() => handleSearch()}
                  disabled={loading || !addressInput.trim()}
                  className="btn btn-primary btn-lg"
                >
                  {loading
                    ? <><div className="spinner" style={{ width: 16, height: 16 }} /> Searching...</>
                    : 'Search'}
                </button>
              </div>
              {error && <div className="alert alert-error" style={{ marginTop: 12, marginBottom: 0 }}>{error}</div>}
            </div>
          </div>
        )}

        {/* ══════════════════════════════════════════════════
            STEP 2 — PROPERTY PREVIEW
        ══════════════════════════════════════════════════ */}
        {step === STEPS.PREVIEW && propertyData && (
          <div style={{ animation: 'fadeIn 0.35s ease' }}>
            <StepBadge current={1} total={3} label="Review Property Details" />

            {/* Search bar (compact) */}
            <div className="card" style={{ marginBottom: 20 }}>
              <div style={{ display: 'flex', gap: 10 }}>
                <AddressAutocomplete
                  value={addressInput}
                  onChange={setAddressInput}
                  onSelect={handlePlaceSelect}
                  disabled={loading}
                />
                <button
                  onClick={() => handleSearch()}
                  disabled={loading || !addressInput.trim()}
                  className="btn btn-primary"
                >
                  {loading ? <><div className="spinner" style={{ width: 14, height: 14 }} /> Searching...</> : 'Search'}
                </button>
              </div>
            </div>

            {/* Side-by-side: property photo + street view */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 20, alignItems: 'stretch' }}>
              {/* Left: property photo carousel */}
              {(() => {
                const apiImgs = propertyData.images?.length ? propertyData.images : (propertyData.imageUrl ? [propertyData.imageUrl] : []);
                const imgs = userImages.length > 0 && apiImgs.length === 0 ? userImages : apiImgs;
                const total = imgs.length;
                const prev = () => setPhotoIndex(i => (i - 1 + total) % total);
                const next = () => setPhotoIndex(i => (i + 1) % total);
                const idx  = Math.min(photoIndex, Math.max(0, total - 1));
                return (
                  <div style={{ borderRadius: 14, overflow: 'hidden', background: 'var(--bg-card)', border: '1px solid var(--border)', height: 300, position: 'relative' }}>
                    {total > 0 ? (
                      <img
                        key={idx}
                        src={imgs[idx]}
                        alt={propertyData.address}
                        referrerPolicy="no-referrer"
                        onClick={() => setLightbox(true)}
                        style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block', cursor: 'zoom-in' }}
                        onError={e => { e.target.style.display = 'none'; }}
                      />
                    ) : (
                      <label style={{ height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 10, cursor: 'pointer', padding: '20px', textAlign: 'center' }}>
                        <input type="file" accept="image/*" multiple style={{ display: 'none' }} onChange={e => {
                          Array.from(e.target.files).forEach(file => {
                            const reader = new FileReader();
                            reader.onload = ev => setUserImages(prev => [...prev, ev.target.result]);
                            reader.readAsDataURL(file);
                          });
                        }} />
                        <div style={{ fontSize: '0.95rem', fontWeight: 700, color: 'var(--text-primary)' }}>Add Image</div>
                        <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', lineHeight: 1.6, maxWidth: 200 }}>
                          Add property images for better accuracy. Without them, results may be less accurate and will need more due diligence.
                        </div>
                        <div style={{ marginTop: 6, padding: '7px 18px', borderRadius: 8, border: '1px solid var(--primary-light)', fontSize: '0.78rem', color: 'var(--primary-light)', fontWeight: 600 }}>
                          Upload Photo
                        </div>
                      </label>
                    )}
                    {/* Gradient + address */}
                    <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, background: 'linear-gradient(transparent 30%, rgba(0,0,0,0.75) 100%)', padding: '24px 16px 14px', pointerEvents: 'none' }}>
                      <div style={{ fontSize: '0.88rem', color: '#fff', fontWeight: 600 }}>{propertyData.address}</div>
                    </div>
                    {/* View fullscreen hint */}
                    {total > 0 && apiImgs.length > 0 && (
                      <div onClick={() => setLightbox(true)} style={{ position: 'absolute', top: 10, left: 10, background: 'rgba(0,0,0,0.5)', color: '#fff', fontSize: '0.7rem', fontWeight: 600, padding: '4px 9px', borderRadius: 8, cursor: 'zoom-in', backdropFilter: 'blur(4px)', letterSpacing: '0.03em' }}>
                        ⛶ View Photos
                      </div>
                    )}
                    {/* Remove user-uploaded image */}
                    {userImages.length > 0 && apiImgs.length === 0 && (
                      <button onClick={() => setUserImages([])} title="Remove image" style={{ position: 'absolute', top: 10, right: 10, background: 'rgba(0,0,0,0.55)', border: 'none', color: '#fff', borderRadius: 6, width: 28, height: 28, cursor: 'pointer', fontSize: 14, display: 'flex', alignItems: 'center', justifyContent: 'center', backdropFilter: 'blur(4px)' }}>✕</button>
                    )}
                    {/* Prev / Next arrows */}
                    {total > 1 && (<>
                      <button onClick={prev} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', background: 'rgba(0,0,0,0.45)', border: 'none', borderRadius: '50%', width: 34, height: 34, cursor: 'pointer', color: '#fff', fontSize: 18, display: 'flex', alignItems: 'center', justifyContent: 'center', backdropFilter: 'blur(4px)' }}>‹</button>
                      <button onClick={next} style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', background: 'rgba(0,0,0,0.45)', border: 'none', borderRadius: '50%', width: 34, height: 34, cursor: 'pointer', color: '#fff', fontSize: 18, display: 'flex', alignItems: 'center', justifyContent: 'center', backdropFilter: 'blur(4px)' }}>›</button>
                      {/* Dot indicators */}
                      <div style={{ position: 'absolute', bottom: 46, left: '50%', transform: 'translateX(-50%)', display: 'flex', gap: 5 }}>
                        {imgs.map((_, i) => (
                          <button key={i} onClick={() => setPhotoIndex(i)} style={{ width: i === idx ? 18 : 6, height: 6, borderRadius: 3, background: i === idx ? '#fff' : 'rgba(255,255,255,0.5)', border: 'none', cursor: 'pointer', padding: 0, transition: 'width 0.2s' }} />
                        ))}
                      </div>
                      <div style={{ position: 'absolute', top: 10, right: 10, background: 'rgba(0,0,0,0.5)', color: '#fff', fontSize: '0.72rem', fontWeight: 600, padding: '3px 8px', borderRadius: 10 }}>{idx + 1} / {total}</div>
                    </>)}
                  </div>
                );
              })()}

              {/* Right: street view */}
              <div style={{ borderRadius: 14, overflow: 'hidden', border: '1px solid var(--border)', height: 300 }}>
                <StreetViewWidget
                  address={propertyData?.address || addressInput}
                  lat={placeInfo.lat}
                  lng={placeInfo.lng}
                />
              </div>
            </div>

            {/* Property chips — NO price/ARV shown */}
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 20 }}>
              {[
                propertyData.propertyType,
                editableFields.beds && `${editableFields.beds} bed`,
                editableFields.baths && `${editableFields.baths} bath`,
                editableFields.sqft && `${Number(editableFields.sqft).toLocaleString()} sqft`,
                editableFields.yearBuilt && `Built ${editableFields.yearBuilt}`,
                propertyData.isMockData && 'Demo data'
              ].filter(Boolean).map(t => (
                <span key={t} style={{
                  padding: '5px 14px', background: 'rgba(37,99,235,0.1)',
                  borderRadius: 20, fontSize: '0.85rem', color: 'var(--primary-light)',
                  fontWeight: 600, border: '1px solid rgba(37,99,235,0.2)'
                }}>{t}</span>
              ))}
            </div>

            {/* Editable fields */}
            <div className="card" style={{ marginBottom: 20 }}>
              <div style={{ fontWeight: 700, fontSize: '0.95rem', color: 'var(--text-primary)', marginBottom: 16 }}>
                Edit Property Details
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 12 }}>
                {[
                  { key: 'beds', label: 'Beds', type: 'number' },
                  { key: 'baths', label: 'Baths', type: 'number' },
                  { key: 'sqft', label: 'Sq Ft', type: 'number' },
                  { key: 'yearBuilt', label: 'Year Built', type: 'number' },
                  { key: 'lotSize', label: 'Lot Size', type: 'text' }
                ].map(f => (
                  <div key={f.key}>
                    <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 5 }}>{f.label}</div>
                    <input
                      type={f.type}
                      className="input"
                      value={editableFields[f.key]}
                      onChange={e => setEditableFields(prev => ({ ...prev, [f.key]: e.target.value }))}
                      style={{ padding: '8px 10px', fontSize: '0.9rem' }}
                    />
                  </div>
                ))}
              </div>
            </div>

            {/* Comp Filters */}
            <div className="card" style={{ marginBottom: 20 }}>
              <div style={{ fontWeight: 700, fontSize: '0.95rem', color: 'var(--text-primary)', marginBottom: 16 }}>
                Comp Filters
              </div>

              {/* Instructions */}
              <div style={{ marginBottom: 16 }}>
                <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}>Instructions</div>
                <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)', marginBottom: 8 }}>Add any custom instructions for generating comps</div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, marginBottom: 10 }}>
                  {[
                    { key: 'matchBedsBaths', label: 'Must match beds/baths exactly' },
                    { key: 'builtWithin10',  label: 'Built within 10 yrs' },
                    { key: 'hasPool',        label: 'Has pool' },
                    { key: 'schoolDistrict', label: 'School district match' },
                  ].map(f => (
                    <label key={f.key} style={{ display: 'flex', alignItems: 'center', gap: 7, cursor: 'pointer', fontSize: '0.85rem', color: 'var(--text-secondary)', background: 'rgba(255,255,255,0.04)', border: '1px solid var(--border)', borderRadius: 8, padding: '6px 12px' }}>
                      <input type="checkbox" checked={compFlags[f.key]} onChange={e => setCompFlags(p => ({ ...p, [f.key]: e.target.checked }))} style={{ accentColor: 'var(--primary)', width: 14, height: 14 }} />
                      {f.label}
                    </label>
                  ))}
                </div>
                <textarea
                  className="input"
                  rows={2}
                  placeholder="e.g. Only use comps on corner lots, exclude distressed sales…"
                  value={compInstructions}
                  onChange={e => setCompInstructions(e.target.value)}
                  style={{ width: '100%', resize: 'vertical', fontSize: '0.875rem', padding: '8px 10px' }}
                />
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 16 }}>
                {/* Mode */}
                <div>
                  <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 }}>Mode</div>
                  <div style={{ display: 'flex', background: 'rgba(255,255,255,0.04)', borderRadius: 8, border: '1px solid var(--border)', padding: 3, gap: 3 }}>
                    {['arv', 'asis'].map(m => (
                      <button key={m} onClick={() => setCompMode(m)} style={{ flex: 1, padding: '7px 0', borderRadius: 6, border: 'none', cursor: 'pointer', fontWeight: 600, fontSize: '0.85rem', background: compMode === m ? 'var(--primary)' : 'transparent', color: compMode === m ? '#fff' : 'var(--text-secondary)', transition: 'all 0.15s' }}>
                        {m === 'arv' ? 'ARV' : 'As-Is'}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Time Frame */}
                <div>
                  <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 }}>Time Frame</div>
                  <div style={{ display: 'flex', gap: 6 }}>
                    {[3, 6, 12, 18].map(m => (
                      <button key={m} onClick={() => setCompTimeFrame(m)} style={{ flex: 1, padding: '7px 0', borderRadius: 6, border: `1px solid ${compTimeFrame === m ? 'var(--primary)' : 'var(--border)'}`, cursor: 'pointer', fontWeight: 600, fontSize: '0.82rem', background: compTimeFrame === m ? 'rgba(37,99,235,0.15)' : 'transparent', color: compTimeFrame === m ? 'var(--primary-light)' : 'var(--text-secondary)', transition: 'all 0.15s' }}>
                        {m}mo
                      </button>
                    ))}
                  </div>
                </div>

                {/* Max Distance */}
                <div>
                  <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 }}>Max Distance</div>
                  <div style={{ display: 'flex', background: 'rgba(255,255,255,0.04)', borderRadius: 8, border: '1px solid var(--border)', padding: 3, gap: 3, marginBottom: 8 }}>
                    {['auto', 'custom'].map(m => (
                      <button key={m} onClick={() => setCompDistMode(m)} style={{ flex: 1, padding: '7px 0', borderRadius: 6, border: 'none', cursor: 'pointer', fontWeight: 600, fontSize: '0.85rem', background: compDistMode === m ? 'var(--primary)' : 'transparent', color: compDistMode === m ? '#fff' : 'var(--text-secondary)', transition: 'all 0.15s', textTransform: 'capitalize' }}>
                        {m}
                      </button>
                    ))}
                  </div>
                  {compDistMode === 'custom' && (
                    <div>
                      <input type="range" min={0.3} max={10} step={0.1} value={compDistance} onChange={e => setCompDistance(parseFloat(e.target.value))}
                        style={{ width: '100%', accentColor: 'var(--primary)', cursor: 'pointer' }} />
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: 3 }}>
                        <span>0.3 mi</span>
                        <span style={{ color: 'var(--primary-light)', fontWeight: 700 }}>{compDistance.toFixed(1)} mi</span>
                        <span>10 mi</span>
                      </div>
                    </div>
                  )}
                </div>

                {/* Property Type */}
                <div>
                  <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 }}>Property Type</div>
                  <select className="input" value={compPropertyType} onChange={e => setCompPropertyType(e.target.value)} style={{ padding: '8px 10px', fontSize: '0.875rem', width: '100%' }}>
                    <option value="auto">Auto</option>
                    <option value="single_family">Single Family</option>
                    <option value="condo">Condo</option>
                    <option value="townhouse">Townhouse</option>
                    <option value="multi_family">Multi Family</option>
                    <option value="land">Land</option>
                  </select>
                </div>
              </div>

              {/* Property Status */}
              <div style={{ marginTop: 16 }}>
                <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 }}>Property Status</div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                  {[
                    { key: 'mlsActive',    label: 'MLS Active' },
                    { key: 'mlsPending',   label: 'MLS Pending' },
                    { key: 'mlsSold',      label: 'MLS Sold' },
                    { key: 'publicRecord', label: 'Public Record' },
                  ].map(s => (
                    <label key={s.key} style={{ display: 'flex', alignItems: 'center', gap: 7, cursor: 'pointer', fontSize: '0.85rem', color: compStatus[s.key] ? 'var(--primary-light)' : 'var(--text-secondary)', background: compStatus[s.key] ? 'rgba(37,99,235,0.1)' : 'rgba(255,255,255,0.04)', border: `1px solid ${compStatus[s.key] ? 'rgba(37,99,235,0.35)' : 'var(--border)'}`, borderRadius: 8, padding: '6px 12px', transition: 'all 0.15s' }}>
                      <input type="checkbox" checked={compStatus[s.key]} onChange={e => setCompStatus(p => ({ ...p, [s.key]: e.target.checked }))} style={{ accentColor: 'var(--primary)', width: 14, height: 14 }} />
                      {s.label}
                    </label>
                  ))}
                </div>
              </div>
            </div>

            {(gateError || compsError) && (
              <div className="alert alert-error" style={{ marginBottom: 16 }}>{gateError || compsError}</div>
            )}

            <button
              onClick={handleConfirmInfo}
              disabled={gateChecking || compsLoading}
              className="btn btn-primary btn-lg"
              style={{ width: '100%', justifyContent: 'center', padding: 16, fontSize: '1rem' }}
            >
              {gateChecking || compsLoading
                ? <><div className="spinner" style={{ width: 18, height: 18 }} /> {gateChecking ? 'Verifying...' : 'Searching for comps…'}</>
                : 'Analyze Property'}
            </button>
          </div>
        )}

        {/* ══════════════════════════════════════════════════
            STEP 3 — RESULTS
        ══════════════════════════════════════════════════ */}
        {step === STEPS.RESULTS && (
          <div style={{ animation: 'fadeIn 0.35s ease' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 0 }}>
              <StepBadge current={2} total={2} label="Deal Analysis" />
              <button onClick={() => setStep(STEPS.PREVIEW)} className="btn btn-ghost btn-sm" style={{ marginBottom: 24 }}>
                Back
              </button>
            </div>

            {/* ── AI METRICS ROW: ARV Range | Rent | ROI | Rehab ── */}
            {(aiMonthlyRent > 0 || aiArvLow > 0) && (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12, marginBottom: 16 }}>
                {/* ARV Range */}
                {aiArvLow > 0 && aiArvHigh > 0 && (
                  <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 12, padding: '14px 16px', textAlign: 'center' }}>
                    <div style={{ fontSize: '0.65rem', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 700, marginBottom: 6 }}>ARV Range</div>
                    <div style={{ fontSize: '1.05rem', fontWeight: 800, color: 'var(--primary-light)' }}>{formatCurrency(aiArvLow)} – {formatCurrency(aiArvHigh)}</div>
                    <div style={{ fontSize: '0.65rem', color: 'var(--text-muted)', marginTop: 3 }}>Mid: {formatCurrency(arvFromComps)}</div>
                  </div>
                )}
                {/* Monthly Rent */}
                <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 12, padding: '14px 16px', textAlign: 'center' }}>
                  <div style={{ fontSize: '0.65rem', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 700, marginBottom: 6 }}>Monthly Rent</div>
                  {aiRentLow > 0 && aiRentHigh > 0
                    ? <div style={{ fontSize: '1.05rem', fontWeight: 800, color: '#10b981' }}>{formatCurrency(aiRentLow)} – {formatCurrency(aiRentHigh)}</div>
                    : <div style={{ fontSize: '1.5rem', fontWeight: 800, color: '#10b981' }}>{aiMonthlyRent > 0 ? formatCurrency(aiMonthlyRent) : '—'}</div>}
                </div>
                {/* Rehab Cost — editable, connected to all calculations */}
                <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 12, padding: '14px 16px' }}>
                  <div style={{ fontSize: '0.65rem', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 700, marginBottom: 8 }}>Rehab Cost</div>
                  <div style={{ position: 'relative' }}>
                    <span style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)', fontSize: '0.9rem', fontWeight: 600 }}>$</span>
                    <input
                      type="number"
                      className="input"
                      value={rehabCost || ''}
                      onChange={e => setRehabCost(Number(e.target.value) || 0)}
                      placeholder="Enter amount"
                      style={{ paddingLeft: 22, fontSize: '0.95rem', fontWeight: 700, width: '100%' }}
                    />
                  </div>
                  {aiRehabLow > 0 && aiRehabHigh > 0 && (
                    <div style={{ fontSize: '0.6rem', color: 'var(--text-muted)', marginTop: 6 }}>
                      AI estimate: {formatCurrency(aiRehabLow)} – {formatCurrency(aiRehabHigh)}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* ── HERO SUMMARY ── */}
            <div style={{ marginBottom: 20, borderRadius: 14, overflow: 'hidden', border: '1px solid var(--border)', background: 'var(--bg-card)' }}>
              <div style={{ display: 'grid', gridTemplateColumns: '280px 1fr' }}>
                {/* Image column — always visible */}
                {(() => {
                  const displayImg = userImages[0] || propertyData?.imageUrl;
                  if (displayImg) {
                    return (
                      <div style={{ position: 'relative', minHeight: 240 }}>
                        <img src={displayImg} alt="" referrerPolicy="no-referrer" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block', minHeight: 240 }} onError={e => e.target.parentNode.style.display = 'none'} />
                        <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to top, rgba(0,0,0,0.55) 0%, transparent 50%)' }} />
                        <div style={{ position: 'absolute', bottom: 12, left: 14 }}>
                          <span style={{ background: 'rgba(0,0,0,0.55)', color: '#fff', fontSize: '0.72rem', fontWeight: 600, padding: '3px 8px', borderRadius: 6 }}>
                            {propertyData?.propertyType || 'Residential'}
                          </span>
                        </div>
                        {userImages[0] && (
                          <button
                            onClick={() => setUserImages([])}
                            title="Remove image"
                            style={{ position: 'absolute', top: 8, right: 8, background: 'rgba(0,0,0,0.55)', border: 'none', color: '#fff', borderRadius: 6, width: 26, height: 26, cursor: 'pointer', fontSize: 13, display: 'flex', alignItems: 'center', justifyContent: 'center', backdropFilter: 'blur(4px)' }}
                          >✕</button>
                        )}
                      </div>
                    );
                  }
                  return (
                    <label style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: 240, cursor: 'pointer', background: 'var(--bg-dark)', borderRight: '1px solid var(--border)', gap: 10, padding: '20px 16px', textAlign: 'center' }}>
                      <input type="file" accept="image/*" multiple style={{ display: 'none' }} onChange={e => {
                        const files = Array.from(e.target.files);
                        files.forEach(file => {
                          const reader = new FileReader();
                          reader.onload = ev => setUserImages(prev => [...prev, ev.target.result]);
                          reader.readAsDataURL(file);
                        });
                      }} />
                      <div style={{ fontSize: '0.88rem', fontWeight: 700, color: 'var(--text-primary)' }}>Add Image</div>
                      <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', lineHeight: 1.5 }}>
                        Add property images for better accuracy.<br />
                        Without them, results may be less accurate<br />
                        and will need more due diligence.
                      </div>
                      <div style={{ marginTop: 4, padding: '6px 14px', borderRadius: 6, border: '1px solid var(--border)', fontSize: '0.73rem', color: 'var(--primary-light)', fontWeight: 600 }}>
                        Upload Photo
                      </div>
                    </label>
                  );
                })()}
                <div style={{ padding: '22px 24px', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                  <div>
                    <div style={{ fontSize: '0.68rem', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 3 }}>Property Address</div>
                    <div style={{ fontSize: '1.05rem', fontWeight: 700, color: 'var(--text-primary)', lineHeight: 1.3, marginBottom: 8 }}>{propertyData?.address || addressInput}</div>
                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 18 }}>
                      {[editableFields.beds && `${editableFields.beds} bed`, editableFields.baths && `${editableFields.baths} bath`, editableFields.sqft && `${Number(editableFields.sqft).toLocaleString()} sqft`, editableFields.yearBuilt && `Built ${editableFields.yearBuilt}`].filter(Boolean).map(t => (
                        <span key={t} style={{ padding: '2px 9px', background: 'rgba(37,99,235,0.1)', borderRadius: 10, fontSize: '0.73rem', color: 'var(--primary-light)', fontWeight: 600 }}>{t}</span>
                      ))}
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10 }}>
                      {[
                        { label: 'ARV', value: formatCurrency(arv), sub: aiArvLow > 0 && aiArvHigh > 0 ? `${formatCurrency(aiArvLow)}–${formatCurrency(aiArvHigh)}` : `${comps.length} comp${comps.length !== 1 ? 's' : ''}`, color: 'var(--primary-light)' },
                        { label: 'Recommended Offer', value: formatCurrency(recommendedOffer), sub: strategyCalc.label, color: '#10b981', big: true },
                        { label: 'Net Profit / Cash Flow', value: strategy === 'hold' ? formatCurrency(cashFlow) + '/mo' : formatCurrency(profit), sub: strategy === 'hold' ? 'Monthly cash flow' : 'After all costs', color: (strategy === 'hold' ? cashFlow : profit) >= 0 ? '#10b981' : '#ef4444' },
                        { label: 'Est. Monthly Rent', value: aiRentLow > 0 && aiRentHigh > 0 ? `${formatCurrency(aiRentLow)}–${formatCurrency(aiRentHigh)}` : (rent > 0 ? formatCurrency(rent) : '—'), sub: aiRentLow > 0 ? 'AI range' : 'Market estimate', color: '#10b981' },
                        { label: 'Strategy', value: strategyCalc.label, sub: 'Selected', color: 'var(--primary-light)' },
                        { label: 'Deal Rating', value: dealRating?.label || '—', sub: pp ? 'Based on spread' : 'Enter purchase price', color: dealRating?.color || 'var(--text-muted)' },
                      ].map(m => (
                        <div key={m.label} style={{ background: 'var(--bg-dark)', borderRadius: 8, padding: '10px 12px', border: m.big ? '1.5px solid rgba(16,185,129,0.35)' : '1px solid var(--border)' }}>
                          <div style={{ fontSize: '0.62rem', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 2 }}>{m.label}</div>
                          <div style={{ fontSize: m.big ? '1.25rem' : '1.05rem', fontWeight: 800, color: m.color, lineHeight: 1.1 }}>{m.value}</div>
                          <div style={{ fontSize: '0.65rem', color: 'var(--text-muted)', marginTop: 2 }}>{m.sub}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: 8, marginTop: 16, paddingTop: 14, borderTop: '1px solid var(--border)' }}>
                    <button onClick={saveDeal} className="btn btn-success" style={{ padding: '8px 18px', fontSize: '0.85rem' }}>Save Deal</button>
                    <PDFReport dealData={{ ...dealData, comps }} />
                    <ContractPDF dealData={{ ...dealData, comps }} />
                  </div>
                </div>
              </div>
            </div>

            {/* ── INVESTMENT STRATEGY (Interactive) ── */}
            <Section title="Investment Strategy">
              {/* Strategy tabs */}
              {(() => {
                const tabs = [
                  { id: 'flip',      label: 'Fix & Flip',  color: '#f59e0b' },
                  { id: 'hold',      label: 'Buy & Hold',  color: '#10b981' },
                  { id: 'brrrr',     label: 'BRRRR',       color: '#2563eb' },
                  { id: 'wholesale', label: 'Wholesale',   color: '#8b5cf6' },
                  { id: 'airbnb',    label: 'Airbnb',      color: '#ef4444' },
                ];
                const active = tabs.find(t => t.id === strategy) || tabs[0];
                const inp = (label, value, setValue, opts = {}) => (
                  <div className="form-group" style={{ margin: 0 }}>
                    <label style={{ fontSize: '0.78rem' }}>{label}</label>
                    <div style={{ position: 'relative' }}>
                      {opts.prefix && <span style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)', fontSize: '0.9rem' }}>{opts.prefix}</span>}
                      {opts.suffix && <span style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)', fontSize: '0.9rem' }}>{opts.suffix}</span>}
                      <input
                        type="number" className="input" value={value}
                        onChange={e => setValue(Number(e.target.value))}
                        min={opts.min ?? 0} max={opts.max} step={opts.step ?? 1}
                        style={{ paddingLeft: opts.prefix ? 24 : undefined, paddingRight: opts.suffix ? 32 : undefined, fontSize: '0.9rem' }}
                      />
                    </div>
                  </div>
                );
                const result = (label, value, color = 'var(--text-primary)', big = false) => (
                  <div style={{ background: 'var(--bg-dark)', borderRadius: 9, padding: '12px 14px', border: `1px solid ${big ? active.color + '44' : 'var(--border)'}` }}>
                    <div style={{ fontSize: '0.68rem', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 3 }}>{label}</div>
                    <div style={{ fontSize: big ? '1.5rem' : '1.1rem', fontWeight: big ? 900 : 700, color }}>{value}</div>
                  </div>
                );

                return (
                  <div>
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 20 }}>
                      {tabs.map(t => (
                        <button key={t.id} onClick={() => setStrategy(t.id)} style={{
                          padding: '9px 18px', borderRadius: 9, cursor: 'pointer', fontFamily: 'inherit', fontWeight: 700, fontSize: '0.85rem', transition: 'all 0.15s',
                          border: `2px solid ${strategy === t.id ? t.color : 'var(--border)'}`,
                          background: strategy === t.id ? `${t.color}18` : 'transparent',
                          color: strategy === t.id ? t.color : 'var(--text-secondary)',
                        }}>{t.label}</button>
                      ))}
                    </div>

                    <div style={{ background: `${active.color}08`, border: `1.5px solid ${active.color}30`, borderRadius: 12, padding: '20px 20px 16px' }}>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
                        {/* Left: inputs */}
                        <div>
                          <div style={{ fontSize: '0.78rem', fontWeight: 700, color: active.color, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 14 }}>
                            {active.label} — Inputs
                          </div>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                            {inp('Purchase Price', pp, v => setPurchasePrice(String(v)), { prefix: '$' })}
                            {inp('Down Payment (%)', downPct, setDownPct, { suffix: '%', min: 0, max: 100, step: 5 })}
                            {inp('Interest Rate (%)', interestRate, setInterestRate, { suffix: '%', min: 1, max: 20, step: 0.125 })}
                            <div style={{ display: 'flex', gap: 6 }}>
                              {[10,15,20,30].map(t => (
                                <button key={t} onClick={() => setLoanTerm(t)} style={{ flex:1, padding:'7px 0', border:`1.5px solid ${loanTerm===t ? 'var(--primary)' : 'var(--border)'}`, borderRadius:7, cursor:'pointer', fontFamily:'inherit', fontWeight:700, fontSize:'0.78rem', background: loanTerm===t ? 'rgba(37,99,235,0.15)' : 'transparent', color: loanTerm===t ? 'var(--primary-light)' : 'var(--text-muted)' }}>{t}yr</button>
                              ))}
                            </div>

                            {strategy === 'flip' && inp('Target Profit ($)', targetProfit, setTargetProfit, { prefix: '$' })}
                            {strategy === 'hold' && inp('Target Cash-on-Cash ROI (%)', targetCocROI, setTargetCocROI, { suffix: '%', min: 1, max: 30 })}
                            {strategy === 'brrrr' && inp('Refinance LTV (%)', refinanceLTV, setRefinanceLTV, { suffix: '%', min: 60, max: 80 })}
                            {strategy === 'wholesale' && inp('Desired Assignment Fee ($)', assignmentFee, setAssignmentFee, { prefix: '$' })}
                            {strategy === 'airbnb' && (<>
                              {inp('Nightly Rate ($)', nightlyRate, setNightlyRate, { prefix: '$' })}
                              {inp('Occupancy Rate (%)', occupancyRate, setOccupancyRate, { suffix: '%', min: 10, max: 100 })}
                            </>)}
                          </div>
                        </div>

                        {/* Right: results */}
                        <div>
                          <div style={{ fontSize: '0.78rem', fontWeight: 700, color: active.color, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 14 }}>
                            {active.label} — Results
                          </div>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                            {result('Recommended Offer', formatCurrency(recommendedOffer), '#10b981', true)}

                            {strategy === 'flip' && (<>
                              {result('ARV', formatCurrency(arv))}
                              {result('Rehab Cost', formatCurrency(rehabCost), '#f59e0b')}
                              {result('Holding Costs', formatCurrency(holdingResult.total), '#f59e0b')}
                              {result('Selling Costs (6%)', formatCurrency(sellingCosts))}
                              {result('Target Profit', formatCurrency(targetProfit), active.color)}
                              {result('Net Profit', formatCurrency(strategyCalc.netProfit), strategyCalc.netProfit >= 0 ? '#10b981' : '#ef4444')}
                              {result('ROI', formatPercent(strategyCalc.flipROI), strategyCalc.flipROI >= 15 ? '#10b981' : '#f59e0b')}
                            </>)}

                            {strategy === 'hold' && (<>
                              {result('Monthly Rent', formatCurrency(rent))}
                              {result('Monthly Expenses', formatCurrency(mortgageResult.monthly + holdingResult.monthly), '#f59e0b')}
                              {result('Net Cash Flow / mo', formatCurrency(strategyCalc.monthlyCF), strategyCalc.monthlyCF >= 0 ? '#10b981' : '#ef4444')}
                              {result('Annual Cash Flow', formatCurrency(strategyCalc.annualCF), strategyCalc.annualCF >= 0 ? '#10b981' : '#ef4444')}
                              {result('Cap Rate', formatPercent(capRate), capRate >= 6 ? '#10b981' : '#f59e0b')}
                            </>)}

                            {strategy === 'brrrr' && (<>
                              {result('Refinance Amount', formatCurrency(strategyCalc.refAmount), active.color)}
                              {result('Cash Pulled Out', formatCurrency(strategyCalc.cashOut), '#10b981')}
                              {result('Cash Left In Deal', formatCurrency(strategyCalc.cashLeft), strategyCalc.cashLeft === 0 ? '#10b981' : '#f59e0b')}
                              {result('Equity Created', formatCurrency(strategyCalc.equity), '#10b981')}
                              {strategyCalc.cashLeft === 0 && result('Infinite Return', 'Yes — 0 cash left!', '#10b981')}
                            </>)}

                            {strategy === 'wholesale' && (<>
                              {result('Your MAO to Seller', formatCurrency(strategyCalc.offerPrice), '#10b981', true)}
                              {result('End Buyer MAO', formatCurrency(strategyCalc.endBuyerMAO))}
                              {result('Assignment Fee (Profit)', formatCurrency(assignmentFee), active.color)}
                              {result('ARV', formatCurrency(arv))}
                              {result('Rehab Cost', formatCurrency(rehabCost), '#f59e0b')}
                            </>)}

                            {strategy === 'airbnb' && (<>
                              {result('Monthly Revenue', formatCurrency(strategyCalc.monthlyRevenue), active.color)}
                              {result('Mgmt Fee (20%)', formatCurrency(strategyCalc.mgmtFee), '#f59e0b')}
                              {result('Total Monthly Expenses', formatCurrency(strategyCalc.monthlyExp), '#f59e0b')}
                              {result('Net Cash Flow / mo', formatCurrency(strategyCalc.strCashFlow), strategyCalc.strCashFlow >= 0 ? '#10b981' : '#ef4444')}
                            </>)}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })()}
            </Section>

            {/* ── KEY METRICS GRID ── */}
            <Section title="Key Metrics">
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12 }}>
                <MetricCard label="Purchase Price"     value={formatCurrency(pp)}                                           color="neutral" />
                <MetricCard label="ARV"                value={formatCurrency(arv)}                                          color="blue" />
                <MetricCard label="Rehab Cost"         value={formatCurrency(rehabCost)}                                    color="warning" sub={rehabCost > 0 ? 'Estimated' : 'Not set'} />
                <MetricCard label="Est. Monthly Rent"  value={formatCurrency(rent)}                                         color="neutral" />
                <MetricCard label="Monthly Expenses"   value={formatCurrency(mortgageResult.monthly + holdingResult.monthly)} color="warning" sub="Mortgage + holding" />
                <MetricCard label="Net Cash Flow / mo" value={formatCurrency(cashFlow)}                                     color={cashFlow >= 0 ? 'positive' : 'negative'} />
                <MetricCard label="Cash-on-Cash ROI"   value={formatPercent(roi)}                                           color={roi >= 15 ? 'positive' : roi >= 8 ? 'warning' : 'negative'} />
                <MetricCard label="Cap Rate"           value={formatPercent(capRate)}                                       color={capRate >= 6 ? 'positive' : capRate >= 4 ? 'warning' : 'negative'} />
                <MetricCard label="Net Profit"         value={formatCurrency(profit)}                                       color={profit >= 0 ? 'positive' : 'negative'} sub="After all costs" />
              </div>
            </Section>

            {/* ── REHAB ESTIMATOR ── */}
            <Section title="AI Rehab Estimator" defaultOpen={false}>
              <RehabEstimator onChange={(total) => setRehabCost(total)} location={propertyLocation} mao={mao} />
            </Section>

            {/* ── HOLDING COSTS ── */}
            <Section title="Holding Cost Estimator" defaultOpen={false}>
              <HoldingCostEstimator values={holdingCosts} onChange={setHoldingCosts} />
            </Section>

            {/* ── COMPARABLE SALES ── */}
            <Section title="Comparable Sales Used" defaultOpen={false}>
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
                  <thead>
                    <tr style={{ borderBottom: '1px solid var(--border)' }}>
                      {['Address', 'Price', 'Sqft', 'Beds/Baths', 'Sold Date', '$/sqft'].map(h => (
                        <th key={h} style={{ padding: '10px 12px', textAlign: 'left', color: 'var(--text-muted)', fontWeight: 600, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.04em' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {selectedComps.map(i => comps[i]).filter(Boolean).map((comp, idx) => (
                      <tr key={idx} style={{ borderBottom: '1px solid var(--border)' }}>
                        <td style={{ padding: '10px 12px', color: 'var(--text-secondary)' }}>{comp.address}</td>
                        <td style={{ padding: '10px 12px', fontWeight: 700, color: '#10b981' }}>{formatCurrency(comp.soldPrice)}</td>
                        <td style={{ padding: '10px 12px', color: 'var(--text-secondary)' }}>{(comp.sqft || 0).toLocaleString()}</td>
                        <td style={{ padding: '10px 12px', color: 'var(--text-secondary)' }}>{comp.beds}/{comp.baths}</td>
                        <td style={{ padding: '10px 12px', color: 'var(--text-muted)' }}>{comp.soldDate || '—'}</td>
                        <td style={{ padding: '10px 12px', color: 'var(--text-muted)' }}>${comp.pricePerSqft}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Section>

            {/* ── AI DEAL ANALYSIS ── */}
            <Section title="AI Deal Analysis">
              {!aiInsight ? (
                <div style={{ textAlign: 'center', padding: '20px 0' }}>
                  <p style={{ color: 'var(--text-secondary)', marginBottom: 16, fontSize: '0.9rem' }}>Get a GPT-4o powered analysis of this deal</p>
                  <button onClick={handleGetAI} disabled={aiLoading || !pp} className="btn btn-primary btn-lg">
                    {aiLoading ? <><div className="spinner" style={{ width: 18, height: 18 }} /> Analyzing…</> : 'Get AI Analysis'}
                  </button>
                  {!pp && <p style={{ fontSize: '0.8rem', color: 'var(--text-muted)', marginTop: 8 }}>Enter a purchase price first</p>}
                </div>
              ) : (
                <div style={{ animation: 'fadeIn 0.3s ease' }}>
                  <div style={{ padding: '18px 20px', background: 'rgba(37,99,235,0.06)', borderRadius: 10, border: '1px solid rgba(37,99,235,0.2)', marginBottom: 12 }}>
                    <div style={{ fontWeight: 600, color: 'var(--primary-light)', marginBottom: 10, fontSize: '0.88rem' }}>AI Analysis — GPT-4o</div>
                    <p style={{ lineHeight: 1.75, color: 'var(--text-secondary)', margin: 0, fontSize: '0.92rem' }}>{aiInsight}</p>
                  </div>
                  <button onClick={handleGetAI} className="btn btn-ghost btn-sm">Regenerate</button>
                </div>
              )}
            </Section>

            {/* ── MORTGAGE CALCULATOR ── */}
            <Section title="Mortgage Calculator" defaultOpen={false}>
              <MortgageCalculator initialPrice={pp || arv * 0.75} initialRate={interestRate} initialTerm={loanTerm} initialDown={downPct} />
            </Section>

          </div>
        )}

        {/* Empty state (initial load) */}
        {step === STEPS.SEARCH && !loading && (
          <div style={{ textAlign: 'center', padding: '40px 20px 0', color: 'var(--text-muted)' }}>
            <p style={{ maxWidth: 400, margin: '0 auto', fontSize: '0.9rem', color: 'var(--text-secondary)' }}>
              Search any property in the US to get comps, ARV, deal score, and AI analysis.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
